"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowLeft, ArrowRight, Building, Building2, Check, Home } from "lucide-react";
import { Button } from "@/components/v2/Button";
import { cn } from "@/lib/utils";
import { reportLead } from "@/lib/metaPixel";
import {
  VALUATION_CONSENT_TEXT,
  VALUATION_STORAGE_KEY,
  validateValuationPayload,
  type ValuationErrors,
} from "@/lib/v2/valuation";

type Values = {
  propertyType: string;
  postalCode: string;
  unitNumber: string;
  unitSize: string;
  renovationYear: string;
  renovationAmount: string;
  hdbMopMet: string;
  hdbYearsStayed: string;
  name: string;
  email: string;
  mobile: string;
  consent: boolean;
};

const INITIAL: Values = {
  propertyType: "",
  postalCode: "",
  unitNumber: "",
  unitSize: "",
  renovationYear: "",
  renovationAmount: "",
  hdbMopMet: "",
  hdbYearsStayed: "",
  name: "",
  email: "",
  mobile: "",
  consent: false,
};

const TYPES = [
  { value: "hdb", label: "HDB", hint: "Any flat type", Icon: Home },
  { value: "ec", label: "EC", hint: "Executive condo", Icon: Building },
  { value: "private-condo", label: "Private condo", hint: "Condo or apartment", Icon: Building2 },
];

const STEPS = ["Property", "Details", "Contact"] as const;
const STEP_FIELDS: Array<Array<keyof Values>> = [
  ["propertyType", "postalCode", "unitNumber", "hdbMopMet", "hdbYearsStayed"],
  ["unitSize", "renovationYear", "renovationAmount"],
  ["name", "email", "mobile", "consent"],
];

const inputClass =
  "min-h-12 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none transition placeholder:text-ink-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-100";
const YEARS = Array.from({ length: 25 }, (_, i) => String(new Date().getFullYear() - i));

function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: string; error?: string; children: React.ReactNode; htmlFor: string }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-semibold text-ink">
        {label} {hint && <span className="font-normal text-ink-faint">({hint})</span>}
      </label>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1.5 text-xs text-sold">{error}</p>}
    </div>
  );
}

export function ValuationForm() {
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState<Values>(INITIAL);
  const [errors, setErrors] = useState<ValuationErrors>({});
  const [sending, setSending] = useState(false);

  const update = (name: keyof Values, value: string | boolean) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined, form: undefined }));
  };

  // Validate the whole payload but only surface errors for the current step.
  const stepErrors = (index: number): ValuationErrors => {
    const { errors: all } = validateValuationPayload({ ...values, consent: index === 2 ? values.consent : true });
    const keys = STEP_FIELDS[index];
    const out: ValuationErrors = {};
    for (const k of keys) if (all[k]) out[k] = all[k];
    return out;
  };

  const next = () => {
    const e = stepErrors(step);
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async () => {
    const validation = validateValuationPayload(values);
    if (!validation.data) {
      setErrors(validation.errors);
      return;
    }
    setSending(true);
    try {
      const res = await fetch("/valuation/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(validation.data),
      });
      const result = (await res.json()) as { ok: boolean; errors?: ValuationErrors };
      if (!res.ok || !result.ok) {
        setErrors(result.errors ?? { form: "We could not send your details. Try again." });
        return;
      }
      reportLead("Valuation request");
      sessionStorage.setItem(VALUATION_STORAGE_KEY, JSON.stringify(validation.data));
      const q = new URLSearchParams({ type: validation.data.propertyType, postal: validation.data.postalCode, unit: validation.data.unitNumber });
      router.push(`/valuation/thanks?${q.toString()}`);
    } catch {
      setErrors({ form: "We could not send your details. Check your connection and try again." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div id="valuation-form" className="scroll-mt-28">
      <ol className="mb-8 grid grid-cols-3 gap-2" aria-label="Progress">
        {STEPS.map((label, i) => {
          const done = i < step;
          const current = i === step;
          return (
            <li key={label} className="flex items-center gap-2">
              <span
                className={cn(
                  "flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                  done ? "bg-avail text-white" : current ? "bg-brand-500 text-white" : "bg-surface text-ink-faint",
                )}
              >
                {done ? <Check aria-hidden="true" className="size-4" /> : i + 1}
              </span>
              <span className={cn("text-xs font-semibold sm:text-sm", current ? "text-ink" : "text-ink-faint")}>{label}</span>
              <span className={cn("ml-1 hidden h-0.5 flex-1 rounded-full sm:block", done ? "bg-avail" : "bg-line")} />
            </li>
          );
        })}
      </ol>

      {step === 0 && (
        <div className="space-y-6">
          <div>
            <p className="text-sm font-semibold text-ink">What kind of home is it?</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {TYPES.map(({ value, label, hint, Icon }) => {
                const on = values.propertyType === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => update("propertyType", value)}
                    aria-pressed={on}
                    className={cn(
                      "flex flex-col items-start gap-3 rounded-2xl border p-4 text-left transition-colors",
                      on ? "border-brand-500 bg-brand-50 ring-2 ring-brand-100" : "border-line bg-white hover:border-ink-faint",
                    )}
                  >
                    <span className={cn("flex size-10 items-center justify-center rounded-xl", on ? "bg-brand-500 text-white" : "bg-surface text-ink-muted")}>
                      <Icon aria-hidden="true" className="size-5" />
                    </span>
                    <span>
                      <span className="block font-display font-semibold text-ink">{label}</span>
                      <span className="block text-xs text-ink-muted">{hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            {errors.propertyType && <p className="mt-1.5 text-xs text-sold">{errors.propertyType}</p>}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Postal code" htmlFor="postalCode" error={errors.postalCode}>
              <input id="postalCode" name="postalCode" inputMode="numeric" maxLength={6} value={values.postalCode} onChange={(e) => update("postalCode", e.target.value)} placeholder="530868" className={inputClass} />
            </Field>
            <Field label="Unit number" htmlFor="unitNumber" error={errors.unitNumber}>
              <input id="unitNumber" name="unitNumber" value={values.unitNumber} onChange={(e) => update("unitNumber", e.target.value)} placeholder="#12-34" className={inputClass} />
            </Field>
          </div>
          {values.propertyType === "hdb" && (
            <div className="space-y-4">
              <fieldset>
                <legend className="text-sm font-semibold text-ink">Has the flat met its Minimum Occupation Period (MOP)?</legend>
                <div className="mt-3 grid grid-cols-2 gap-3 sm:max-w-xs">
                  {(["yes", "no"] as const).map((answer) => {
                    const on = values.hdbMopMet === answer;
                    return (
                      <button
                        key={answer}
                        type="button"
                        onClick={() => update("hdbMopMet", answer)}
                        aria-pressed={on}
                        className={cn(
                          "min-h-12 rounded-xl border px-4 font-semibold transition-colors",
                          on ? "border-brand-500 bg-brand-50 text-ink ring-2 ring-brand-100" : "border-line bg-white text-ink-muted hover:border-ink-faint",
                        )}
                      >
                        {answer === "yes" ? "Yes" : "Not yet"}
                      </button>
                    );
                  })}
                </div>
                {errors.hdbMopMet && <p className="mt-1.5 text-xs text-sold">{errors.hdbMopMet}</p>}
              </fieldset>
              {values.hdbMopMet === "no" && (
                <Field label="How many years have you lived there?" htmlFor="hdbYearsStayed" error={errors.hdbYearsStayed}>
                  <input id="hdbYearsStayed" name="hdbYearsStayed" inputMode="numeric" maxLength={2} value={values.hdbYearsStayed} onChange={(e) => update("hdbYearsStayed", e.target.value)} placeholder="3" className={cn(inputClass, "sm:max-w-xs")} />
                </Field>
              )}
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">All optional. Renovation feeds the depreciation schedule in your report, so a rough figure helps.</p>
          <Field label="Unit size" hint="sqft" htmlFor="unitSize" error={errors.unitSize}>
            <input id="unitSize" name="unitSize" inputMode="numeric" value={values.unitSize} onChange={(e) => update("unitSize", e.target.value)} placeholder="1,001" className={inputClass} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Last renovation" hint="year" htmlFor="renovationYear" error={errors.renovationYear}>
              <select id="renovationYear" name="renovationYear" value={values.renovationYear} onChange={(e) => update("renovationYear", e.target.value)} className={inputClass}>
                <option value="">Choose a year</option>
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Field>
            <Field label="Renovation amount" hint="S$" htmlFor="renovationAmount" error={errors.renovationAmount}>
              <input id="renovationAmount" name="renovationAmount" inputMode="numeric" value={values.renovationAmount} onChange={(e) => update("renovationAmount", e.target.value)} placeholder="60,000" className={inputClass} />
            </Field>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <Field label="Name" htmlFor="name" error={errors.name}>
            <input id="name" name="name" autoComplete="name" value={values.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" className={inputClass} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email" htmlFor="email" error={errors.email}>
              <input id="email" name="email" type="email" autoComplete="email" value={values.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" className={inputClass} />
            </Field>
            <Field label="Mobile" htmlFor="mobile" error={errors.mobile}>
              <input id="mobile" name="mobile" inputMode="tel" autoComplete="tel" value={values.mobile} onChange={(e) => update("mobile", e.target.value)} placeholder="+65 9123 4567" className={inputClass} />
            </Field>
          </div>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-surface p-4 text-sm text-ink-muted">
            <input type="checkbox" name="consent" checked={values.consent} onChange={(e) => update("consent", e.target.checked)} className="mt-0.5 size-4 rounded border-line text-brand-500 focus:ring-brand-500" />
            <span>{VALUATION_CONSENT_TEXT}</span>
          </label>
          {errors.consent && <p className="text-xs text-sold">{errors.consent}</p>}
        </div>
      )}

      {errors.form && <p className="mt-4 text-sm text-sold">{errors.form}</p>}

      <div className="mt-8 flex items-center justify-between gap-3">
        {step > 0 ? (
          <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>
            <ArrowLeft aria-hidden="true" className="size-4" /> Back
          </Button>
        ) : (
          <span />
        )}
        {step < STEPS.length - 1 ? (
          <Button onClick={next} size="lg">
            Continue <ArrowRight aria-hidden="true" className="size-4" />
          </Button>
        ) : (
          <Button onClick={submit} size="lg" disabled={sending}>
            {sending ? "Sending..." : "Get my AI valuation report"}
          </Button>
        )}
      </div>
      <p className="mt-4 text-xs text-ink-faint">No login. No OTP. Winnie checks every report before it is sent.</p>
    </div>
  );
}
